import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Star, Info, Heart, ChevronLeft, ChevronRight, Clock, Calendar, RotateCcw, X } from 'lucide-react';
import { categories, getWatchProgress } from '../mock/movies';

// Hook para featuredMovies estable
const useFeaturedMovies = () => {
  return useMemo(() => [
    ...categories.action.slice(0, 2),
    ...categories.drama.slice(0, 2),
    ...categories.horror.slice(0, 2),
    ...categories.comedy.slice(0, 1)
  ].map(movie => ({
    ...movie,
    duration: movie.duration || "2h 00m",
    year: movie.year || 2024,
    genres: movie.genres || ["Acción"]
  })), []);
};

const LazyImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`${className} relative overflow-hidden`}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-gray-600 border-t-red-500 rounded-full animate-spin"></div>
        </div>
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`${className} transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
        />
      )}
    </div>
  );
};

// Modal de Detalles
const MovieDetailModal = ({ movie, isOpen, onClose, onPlayVideo, onToggleFavorite, isFavorite, progress }) => {
  const hasProgress = progress > 0;

  
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-gray-900 to-black rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Botón Cerrar */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm border border-white/20 text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Imagen de Fondo */}
          <div className="relative h-96">
            <img
              src={movie.image}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
          </div>

          {/* Contenido */}
          <div className="p-8 -mt-32 relative z-10">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {movie.title}
            </h2>

            {/* Progress Indicator */}
            {hasProgress && (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <RotateCcw className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-semibold text-sm">Continuar viendo</span>
                  <span className="text-gray-400 text-sm">{progress}% completado</span>
                </div>
                <div className="w-full bg-gray-700/50 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Movie Info */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2 bg-yellow-500/20 px-3 py-1 rounded-full">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="font-bold text-yellow-400">{movie.rating}/10</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-500/20 px-3 py-1 rounded-full">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span className="font-medium text-blue-400">{movie.year}</span>
              </div>
              <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
                <Clock className="w-4 h-4 text-green-400" />
                <span className="font-medium text-green-400">{movie.duration}</span>
              </div>
            </div>

            {/* Genres */}
            <div className="flex flex-wrap gap-2 mb-6">
              {movie.genres?.map((genre, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-200 border border-white/10"
                >
                  {genre}
                </span>
              ))}
            </div>

            {/* Descripción Completa */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-3">Sinopsis</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                {movie.description}
              </p>
            </div>

            {/* Botones de Acción */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => {
                  onPlayVideo(movie, progress);
                  onClose();
                }}
                className="bg-white text-black px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-gray-100 transition-all flex-1"
              >
                <Play className="w-6 h-6" />
                {hasProgress ? 'Continuar' : 'Reproducir Ahora'}
              </button>

              <button
                onClick={() => onToggleFavorite(movie.id)}
                className={`px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all flex-1 ${
                  isFavorite 
                    ? 'bg-red-500/20 text-red-400 border-2 border-red-500/50' 
                    : 'bg-gray-600/80 text-white border-2 border-gray-500/50'
                }`}
              >
                <Heart className={`w-6 h-6 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? 'En Mi Lista' : 'Agregar a Mi Lista'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Hero = ({ onPlayVideo, onToggleFavorite, favorites = [], watchProgress = {} }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const autoPlayRef = useRef(null);

  const featuredMovies = useFeaturedMovies();
  const currentMovie = featuredMovies[currentIndex];
  const isFavorite = favorites.includes(currentMovie?.id);
  const progress = watchProgress[currentMovie?.id] || getWatchProgress(currentMovie?.id) || 0;
  const hasProgress = progress > 0;

    // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Configurar autoplay - CORREGIDO
  useEffect(() => {
    if (featuredMovies.length <= 1 || !isAutoPlaying || isModalOpen) {
      return;
    }

    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredMovies.length);
    }, 8000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [featuredMovies.length, isAutoPlaying, isModalOpen]);

  const handleElementHover = (element) => {
    setHoveredElement(element);
  };

  const handleElementLeave = () => {
    setHoveredElement(null);
  };

  const getButtonStyle = (element) => {
    const isHovered = hoveredElement === element;
    return {
      scale: isHovered ? 1.05 : 1,
      y: isHovered ? -2 : 0,
      transition: { type: "spring", stiffness: 400, damping: 25 }
    };
  };

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % featuredMovies.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + featuredMovies.length) % featuredMovies.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  if (!currentMovie) {
    return (
      <section className="relative min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-600 border-t-red-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Cargando contenido...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      {/* Background Images con transiciones */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentMovie.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <LazyImage
            src={currentMovie.image}
            alt={currentMovie.title}
            className="w-full h-full object-cover"
          />
        </motion.div>
      </AnimatePresence>
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-black/80" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />

      {/* Navigation Arrows */}
      {featuredMovies.length > 1 && (
        <>
          <motion.button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm border border-white/20 text-white transition-all duration-300 hidden lg:flex items-center justify-center"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronLeft className="w-6 h-6" />
          </motion.button>

          <motion.button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm border border-white/20 text-white transition-all duration-300 hidden lg:flex items-center justify-center"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronRight className="w-6 h-6" />
          </motion.button>
        </>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 pb-8 lg:pb-20 pt-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentMovie.id}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl"
          >
            {/* Title */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight drop-shadow-2xl">
              {currentMovie.title}
            </h1>

            {/* Progress Indicator */}
            {hasProgress && (
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <RotateCcw className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-semibold text-sm">Continuar viendo</span>
                  <span className="text-gray-400 text-sm">{progress}% completado</span>
                </div>
                <div className="w-full bg-gray-700/50 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </motion.div>
            )}

            {/* Movie Info */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6 text-white">
              <motion.div 
                className="flex items-center gap-2 bg-yellow-500/20 px-3 py-1 rounded-full backdrop-blur-sm cursor-pointer"
                onHoverStart={() => handleElementHover('rating')}
                onHoverEnd={handleElementLeave}
                whileHover={{ scale: 1.05 }}
              >
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="font-bold text-yellow-400">{currentMovie.rating}/10</span>
              </motion.div>

              <motion.div 
                className="flex items-center gap-2 bg-blue-500/20 px-3 py-1 rounded-full backdrop-blur-sm cursor-pointer"
                onHoverStart={() => handleElementHover('year')}
                onHoverEnd={handleElementLeave}
                whileHover={{ scale: 1.05 }}
              >
                <Calendar className="w-4 h-4 text-blue-400" />
                <span className="font-medium text-blue-400">{currentMovie.year}</span>
              </motion.div>

              <motion.div 
                className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full backdrop-blur-sm cursor-pointer"
                onHoverStart={() => handleElementHover('duration')}
                onHoverEnd={handleElementLeave}
                whileHover={{ scale: 1.05 }}
              >
                <Clock className="w-4 h-4 text-green-400" />
                <span className="font-medium text-green-400">{currentMovie.duration}</span>
              </motion.div>
            </div>

            {/* Genres */}
            <div className="flex flex-wrap gap-2 mb-6">
              {currentMovie.genres?.map((genre, index) => (
                <motion.span 
                  key={index}
                  className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-200 backdrop-blur-sm cursor-pointer border border-white/10"
                  onHoverStart={() => handleElementHover(`genre-${index}`)}
                  onHoverEnd={handleElementLeave}
                  whileHover={{ 
                    scale: 1.05, 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderColor: 'rgba(255,255,255,0.3)'
                  }}
                >
                  {genre}
                </motion.span>
              ))}
            </div>

            {/* Description - Truncada a 2 líneas */}
            <motion.p 
              className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed drop-shadow-lg cursor-pointer line-clamp-2"
              onHoverStart={() => handleElementHover('description')}
              onHoverEnd={handleElementLeave}
              whileHover={{ color: '#f8fafc', scale: 1.01 }}
            >
              {currentMovie.description}
            </motion.p>

            {/* Action Buttons */}
            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              <motion.button
                onClick={() => onPlayVideo(currentMovie, progress)}
                onHoverStart={() => handleElementHover('play')}
                onHoverEnd={handleElementLeave}
                className="group bg-white text-black px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-gray-100 transition-all duration-300 shadow-2xl relative overflow-hidden min-h-[60px] lg:flex-1 lg:max-w-[280px]"
                animate={getButtonStyle('play')}
                whileTap={{ scale: 0.95 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                <Play className="w-6 h-6 group-hover:scale-110 transition-transform z-10 flex-shrink-0" />
                <span className="z-10 whitespace-nowrap text-base lg:text-lg">
                  {hasProgress ? 'Continuar' : 'Reproducir Ahora'}
                </span>
              </motion.button>

              <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 lg:flex-1 lg:max-w-[400px]">
                <motion.button
                  onClick={() => setIsModalOpen(true)}
                  onHoverStart={() => handleElementHover('info')}
                  onHoverEnd={handleElementLeave}
                  className="group bg-gray-600/80 text-white px-6 py-4 rounded-xl font-semibold flex items-center justify-center gap-3 hover:bg-gray-600 transition-all duration-300 backdrop-blur-sm border border-gray-500/50 relative overflow-hidden min-h-[60px] flex-1"
                  animate={getButtonStyle('info')}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <Info className="w-5 h-5 group-hover:scale-110 transition-transform z-10 flex-shrink-0" />
                  <span className="z-10 whitespace-nowrap text-base">Más Información</span>
                </motion.button>

                <motion.button
                  onClick={() => onToggleFavorite(currentMovie.id)}
                  onHoverStart={() => handleElementHover('favorite')}
                  onHoverEnd={handleElementLeave}
                  className={`group ${
                    isFavorite 
                      ? 'bg-red-500/20 text-red-400 border-red-500/50' 
                      : 'bg-gray-600/80 text-white border-gray-500/50'
                  } px-6 py-4 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all duration-300 backdrop-blur-sm border relative overflow-hidden min-h-[60px] flex-1`}
                  animate={getButtonStyle('favorite')}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <Heart 
                    className={`w-5 h-5 transition-all z-10 flex-shrink-0 ${
                      isFavorite 
                        ? 'fill-red-500 text-red-500 scale-110' 
                        : 'group-hover:scale-110'
                    }`} 
                  />
                  <span className="z-10 whitespace-nowrap text-base">
                    {isFavorite ? 'En Mi Lista' : 'Mi Lista'}
                  </span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Indicators */}
        {featuredMovies.length > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {featuredMovies.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-white scale-125' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Floating Elements */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="absolute bottom-8 right-8 hidden lg:flex flex-col gap-4"
      >
        <motion.button
          onClick={() => onToggleFavorite(currentMovie.id)}
          onHoverStart={() => handleElementHover('floating-favorite')}
          onHoverEnd={handleElementLeave}
          className={`p-4 rounded-full backdrop-blur-sm border relative overflow-hidden ${
            isFavorite 
              ? 'bg-red-500/20 border-red-500/50 text-red-400' 
              : 'bg-black/40 border-white/20 text-white'
          } transition-all duration-300`}
          animate={getButtonStyle('floating-favorite')}
          whileTap={{ scale: 0.9 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-[-100%] hover:translate-x-[100%] transition-transform duration-1000" />
          <Heart className={`w-6 h-6 relative z-10 ${isFavorite ? 'fill-current' : ''}`} />
        </motion.button>
      </motion.div>

      {/* Modal de Detalles */}
      <MovieDetailModal
        movie={currentMovie}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onPlayVideo={onPlayVideo}
        onToggleFavorite={onToggleFavorite}
        isFavorite={isFavorite}
        progress={progress}
      />
    </section>
  );
};

export default Hero;